/**
 * Referral landing page (REF-04) — where a referral QR code / link lands.
 *
 * https://app.mediclynk.com/ref/{code} is PUBLIC and is opened by patients on their phones, not by staff,
 * so it is bilingual (ES/EN, following the browser) and never mentions the staff portal.
 *
 * It used to ask `GET /referral/identity?code=…` (the caller's own identity, login required) for the
 * code and fired an anonymous attribution, so every scan ended in "This referral link could not be
 * loaded". It now asks the public `GET /referral/public/:code`, shows the code (with a working copy
 * button), opens the app through its custom scheme, and links to the store. The code is attributed by the
 * app itself, after the person signs in (it keeps the code as pending until then).
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LogoMark } from '../components/LogoMark';
import { apiRequest } from '../lib/apiClient';

export type Lang = 'en' | 'es';
export type ReferralKind = 'patient' | 'specialist' | 'recruitment';

export interface PublicCodeLookup {
  valid: boolean;
  code?: string;
  kind?: ReferralKind;
}

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.mediclynkos.app';

/** The API client unwraps one `{ data }` layer and the backend wraps responses twice: peel what is left. */
export function parseLookup(raw: unknown): PublicCodeLookup {
  let node = raw as Record<string, unknown> | null | undefined;
  for (let depth = 0; depth < 3 && node && typeof node === 'object'; depth += 1) {
    if (typeof node.valid === 'boolean') {
      return {
        valid: node.valid,
        code: typeof node.code === 'string' ? node.code : undefined,
        kind:
          node.kind === 'patient' || node.kind === 'specialist' || node.kind === 'recruitment'
            ? node.kind
            : undefined,
      };
    }
    node = node.data as Record<string, unknown> | undefined;
  }
  return { valid: false };
}

export function detectLang(languages: readonly string[] | undefined): Lang {
  const first = (languages?.[0] ?? '').toLowerCase();
  return first.startsWith('en') ? 'en' : 'es';
}

const COPY: Record<Lang, Record<string, string>> = {
  es: {
    invitedPatient: '¡Te invitaron a MedicLynkOS!',
    invitedSpecialist: '¡Un especialista te invitó a MedicLynkOS!',
    invitedRecruit: '¡Te invitaron a unirte a MedicLynkOS como especialista!',
    lead: 'Atención médica con especialistas, planes de cuidado y seguimiento de tu salud, todo en una app.',
    yourCode: 'Tu código de invitación',
    copy: 'Copiar código',
    copied: '¡Código copiado!',
    steps: 'Descarga la app, crea tu cuenta y escribe este código al registrarte.',
    openApp: 'Abrir en la app',
    openHint: '¿No se abrió? Instala la app y vuelve a tocar este botón.',
    getAndroid: 'Descargar en Google Play',
    iosSoon: 'La app para iPhone llegará pronto.',
    invalidTitle: 'Este enlace de invitación ya no es válido',
    invalidBody:
      'El código no existe o fue reemplazado. Pídele a quien te invitó que te comparta su código actual.',
    errorTitle: 'No pudimos cargar la invitación',
    errorBody: 'Revisa tu conexión e inténtalo de nuevo.',
    retry: 'Reintentar',
    loading: 'Cargando invitación…',
    terms: 'Al registrarte aceptas los Términos de Servicio y la Política de Privacidad.',
  },
  en: {
    invitedPatient: "You've been invited to MedicLynkOS!",
    invitedSpecialist: 'A specialist invited you to MedicLynkOS!',
    invitedRecruit: "You've been invited to join MedicLynkOS as a specialist!",
    lead: 'Care from medical specialists, personalised care plans and health tracking, all in one app.',
    yourCode: 'Your invitation code',
    copy: 'Copy code',
    copied: 'Code copied!',
    steps: 'Download the app, create your account and enter this code when you sign up.',
    openApp: 'Open in the app',
    openHint: "Didn't open? Install the app and tap this button again.",
    getAndroid: 'Get it on Google Play',
    iosSoon: 'The iPhone app is coming soon.',
    invalidTitle: 'This invitation link is no longer valid',
    invalidBody: 'The code does not exist or was replaced. Ask whoever invited you for their current code.',
    errorTitle: "We couldn't load the invitation",
    errorBody: 'Check your connection and try again.',
    retry: 'Try again',
    loading: 'Loading invitation…',
    terms: 'By signing up you agree to the Terms of Service and the Privacy Policy.',
  },
};

export const headlineKey = (kind?: ReferralKind): string =>
  kind === 'specialist' ? 'invitedSpecialist' : kind === 'recruitment' ? 'invitedRecruit' : 'invitedPatient';

const page: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '3rem 1rem 1rem',
  background: '#eff6ff',
  fontFamily: "'Sora', system-ui, sans-serif",
};
const card: React.CSSProperties = {
  background: '#ffffff',
  borderRadius: '12px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  padding: '2rem',
  maxWidth: '28rem',
  width: '100%',
  textAlign: 'center',
};
const primary: React.CSSProperties = {
  width: '100%',
  padding: '1rem',
  backgroundColor: '#0f766e',
  color: '#ffffff',
  fontWeight: 600,
  borderRadius: '8px',
  border: 'none',
  cursor: 'pointer',
  fontSize: '1rem',
  textDecoration: 'none',
  display: 'block',
  boxSizing: 'border-box',
};
const secondary: React.CSSProperties = {
  ...primary,
  backgroundColor: '#f3f4f6',
  color: '#374151',
  fontSize: '0.9rem',
  padding: '0.75rem',
};

export function ReferralLandingPage() {
  const { code } = useParams<{ code: string }>();
  const [lang, setLang] = useState<Lang>(() =>
    detectLang(typeof navigator === 'undefined' ? undefined : navigator.languages ?? [navigator.language]),
  );
  const [state, setState] = useState<'loading' | 'valid' | 'invalid' | 'error'>('loading');
  const [lookup, setLookup] = useState<PublicCodeLookup>({ valid: false });
  const [copied, setCopied] = useState(false);
  const t = COPY[lang];

  const load = async () => {
    if (!code) {
      setState('invalid');
      return;
    }
    setState('loading');
    try {
      const raw = await apiRequest<unknown>(`/referral/public/${encodeURIComponent(code)}`, {
        requiresAuth: false,
      });
      const result = parseLookup(raw);
      setLookup(result);
      setState(result.valid ? 'valid' : 'invalid');
    } catch (err) {
      console.error('Failed to load referral code', err);
      setState('error');
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const shownCode = lookup.code ?? code?.toUpperCase() ?? '';

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(shownCode);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard blocked (non-secure context, old browser): select the text so a long press can copy it.
      const node = document.getElementById('referral-code');
      if (node) window.getSelection()?.selectAllChildren(node);
    }
  };

  const langSwitch = (
    <button
      type="button"
      onClick={() => setLang(lang === 'es' ? 'en' : 'es')}
      style={{ position: 'absolute', top: '0.75rem', right: '1rem', background: 'transparent', border: 'none', color: '#475569', cursor: 'pointer', fontSize: '0.85rem' }}
      aria-label={lang === 'es' ? 'Switch to English' : 'Cambiar a español'}
    >
      {lang === 'es' ? 'English' : 'Español'}
    </button>
  );

  if (state === 'loading') {
    return (
      <div style={page}>
        {langSwitch}
        <div>
          <LogoMark size={64} />
          <p style={{ marginTop: '1rem', color: '#4b5563' }}>{t.loading}</p>
        </div>
      </div>
    );
  }

  if (state === 'invalid' || state === 'error') {
    const isError = state === 'error';
    return (
      <div style={page}>
        {langSwitch}
        <div style={card}>
          <LogoMark size={64} />
          <h1 style={{ marginTop: '1rem', fontSize: '1.4rem', fontWeight: 700, color: '#111827' }}>
            {isError ? t.errorTitle : t.invalidTitle}
          </h1>
          <p style={{ marginTop: '0.75rem', color: '#4b5563' }}>{isError ? t.errorBody : t.invalidBody}</p>
          {isError && (
            <button type="button" onClick={() => void load()} style={{ ...primary, marginTop: '1.5rem' }}>
              {t.retry}
            </button>
          )}
          <a href={PLAY_STORE_URL} style={{ ...secondary, marginTop: '1rem' }}>
            {t.getAndroid}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div style={page}>
      {langSwitch}
      <div style={card}>
        <LogoMark size={64} />
        <h1 style={{ marginTop: '1rem', fontSize: '1.6rem', fontWeight: 700, color: '#111827' }}>
          {t[headlineKey(lookup.kind)]}
        </h1>
        <p style={{ marginTop: '0.5rem', color: '#4b5563' }}>{t.lead}</p>

        <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#ecfeff', borderRadius: '8px', border: '1px solid #a5f3fc' }}>
          <div style={{ fontSize: '0.8rem', color: '#374151' }}>{t.yourCode}</div>
          <div
            id="referral-code"
            style={{ marginTop: '0.35rem', fontFamily: 'monospace', fontWeight: 700, fontSize: '1.5rem', color: '#134e4a', letterSpacing: '0.05em', userSelect: 'all' }}
          >
            {shownCode}
          </div>
          <button type="button" onClick={() => void copyCode()} style={{ ...secondary, marginTop: '0.75rem' }}>
            {copied ? t.copied : t.copy}
          </button>
        </div>

        <p style={{ marginTop: '1.25rem', color: '#374151' }}>{t.steps}</p>

        <div style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <a href={`mediclynk://ref/${encodeURIComponent(shownCode)}`} style={primary}>
            {t.openApp}
          </a>
          <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{t.openHint}</span>
          <a href={PLAY_STORE_URL} style={secondary}>
            {t.getAndroid}
          </a>
          <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{t.iosSoon}</span>
        </div>

        <p style={{ marginTop: '1.5rem', fontSize: '0.75rem', color: '#6b7280' }}>{t.terms}</p>
      </div>
    </div>
  );
}
